import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import styles from "../stylesheets/components/highlights-layout-8.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  HighlightsDisplayType,
  HighlightsItem,
  Language,
  RichEditorState,
  TitleAndSubtitleTextAlign,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import CroppedImage from "./CroppedImage.js";
import HighlightHeading from "./HighlightHeading.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ReadMoreCollapse from "./ReadMoreCollapse.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  isFirstOnPage: boolean;
  scheme: ColorScheme;
  items: HighlightsItem[];
  collapsedLinesCount: number | undefined;
  languageId: Language;
  richEditor: RichEditorState;
  isPreview: boolean;
  queries: ContainerQueries;
  activeModuleId: string | undefined;
  maxColumnsCount: number;
  displayType: HighlightsDisplayType;
  transparentSliderArrowBackground: boolean;
}

const HighlightsLayout8: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  items,
  collapsedLinesCount,
  languageId,
  richEditor,
  isPreview,
  queries,
  activeModuleId,
  maxColumnsCount,
  displayType,
  transparentSliderArrowBackground,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();

  const columns = getColumnsCount(queries, maxColumnsCount);
  const hasHighlightsTitle = !!title;

  const highlights = items.map(
    (
      { id, title, subtitle, picture, imageDetail, buttons, text, textAlign },
      index,
    ) => (
      <article key={id} className={styles.HighlightsLayout8__Item}>
        <div className={styles.HighlightsLayout8__ItemPicture}>
          <CroppedImage
            aspectRatio={undefined}
            picture={picture}
            width={1280}
            detail={imageDetail}
            sizes="(min-width: 640px) 50vw, (min-width: 1024px) 33vw, (min-width: 1600px) 500px, 100vw"
          />
        </div>

        <div
          className={ClassNames(
            styles.HighlightsLayout8__ItemContent,
            css({
              background: scheme.main.background,
              "&::before": { borderColor: scheme.main.background },
            }),
          )}
        >
          <HighlightHeading
            title={{
              value: title,
              className: ClassNames(
                styles.HighlightsLayout8__ItemTitle,
                `TextAlign--${textAlign.title}`,
                css({ color: scheme.main.title }),
              ),
              children: title,
            }}
            subtitle={{
              value: subtitle,
              className: ClassNames(
                styles.HighlightsLayout8__ItemSubtitle,
                `TextAlign--${textAlign.subtitle}`,
              ),
            }}
            highlightIndex={index}
            hasHighlightsTitle={hasHighlightsTitle}
            isFirstOnPage={isFirstOnPage}
          />

          {text && (
            <div>
              {collapsedLinesCount !== undefined ? (
                <ReadMoreCollapse
                  backgroundColor={scheme.main.background}
                  languageId={languageId}
                  collapsedLinesCount={collapsedLinesCount}
                  isEditing={!!richEditor[id]}
                >
                  {text}
                </ReadMoreCollapse>
              ) : (
                text
              )}
            </div>
          )}
          {!!buttons.length && (
            <div className={styles.HighlightsLayout8__Buttons}>{buttons}</div>
          )}
        </div>
      </article>
    ),
  );

  return (
    <div
      className={css({
        background: scheme.main.separator,
        color: scheme.main.text,
      })}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <div className={styles.HighlightsLayout8__Items}>
          {displayType === "slider" && (
            <Slider
              containerClassName={styles.HighlightsLayout8__SliderContainer}
              slideClassName={styles.HighlightsLayout8__Slide}
              isPreview={isPreview}
              sliderEffect="slide"
              onSliderLoaded={setSlider}
              slidesToShow={columns}
              activePreviewSlideId={activeModuleId}
              slideIds={items.map(({ id }) => id)}
            >
              {highlights}
            </Slider>
          )}
          {displayType === "list" && (
            <div
              className={ClassNames(styles.HighlightsLayout8__List, {
                [styles["HighlightsLayout8__List--columns-2"]]: columns === 2,
              })}
            >
              {highlights}
            </div>
          )}
          {displayType === "slider" && items.length > 1 && (
            <div>
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout8__SliderArrow,
                  styles["HighlightsLayout8__SliderArrow--left"],
                )}
                iconClassName={styles.HighlightsLayout8__SliderArrowIcon}
                direction="left"
                onClick={() => slider?.scrollPrev()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout8__SliderArrow,
                  styles["HighlightsLayout8__SliderArrow--right"],
                )}
                iconClassName={styles.HighlightsLayout8__SliderArrowIcon}
                direction="right"
                onClick={() => slider?.scrollNext()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getColumnsCount = (queries: ContainerQueries, maxColumnsCount: number) =>
  queries["Query--xlarge"] ? maxColumnsCount : 1;

export default HighlightsLayout8;
